// useAuthedImageUrl — fetch an authed file from the backend and expose
// it as a browser-local object URL suitable for <img src>.
//
// Why: <img src=...> can't carry custom headers, so we can't directly
// point it at /api/uploads/* (which is Firebase-Bearer-authed). Instead
// we fetch the bytes via authFetch (which adds the Authorization header),
// wrap the response Blob in URL.createObjectURL, and use that.
//
// Returns a small state object so the UI can distinguish:
//   - "loading" → still fetching (show a spinner)
//   - "loaded"  → url is ready (render the image)
//   - "error"   → 404 / 401 / network failure (show a broken-image
//                 placeholder, NOT an eternal spinner — the old behavior
//                 returned undefined forever, which was undiagnosable: a
//                 missing/relocated file looked identical to a slow load).
//
// Lifecycle: on serverPath change or unmount, the previous object URL is
// revoked so we don't leak memory across a long, image-heavy session.
//
// Skipped (status stays "loading", url undefined) when:
//   - serverPath is falsy (caller already has a blobUrl, or there's no file)
//   - serverPath has < 2 path segments → "error" (defensive; must look like
//     `/home/.../inbox/<subdir>/<filename>`)
//
// Used by chat-history image rendering. Never affects the send pipeline.

import { useEffect, useState } from "react";
import { authFetch } from "../auth-context";

// Concurrency limiter for image fetches. Opening an image-heavy conversation
// (a proof-pack thread, the "my-hands images" case) mounts one useAuthedImageUrl
// per image, each firing an authFetch (getIdToken + blob download +
// createObjectURL) IMMEDIATELY and in PARALLEL — dozens at once. On mobile data
// that saturates the connection, delays first paint, and competes with the live
// SSE stream (the "lagging" symptom). Cap concurrent image fetches; queue the
// rest FIFO so a few visible images load fast while a long backlog drains in the
// background. Module-level (shared across all mounted image hooks).
const MAX_CONCURRENT_IMAGE_FETCHES = 4;
let activeImageFetches = 0;
const imageFetchWaiters: Array<() => void> = [];
function pumpImageQueue(): void {
  while (activeImageFetches < MAX_CONCURRENT_IMAGE_FETCHES && imageFetchWaiters.length > 0) {
    activeImageFetches++;
    const resolve = imageFetchWaiters.shift()!;
    resolve();
  }
}
/** Acquire a fetch slot — resolves immediately if under the cap, else queues. */
function acquireImageSlot(): Promise<void> {
  return new Promise<void>((resolve) => {
    imageFetchWaiters.push(resolve);
    pumpImageQueue();
  });
}
/** Release a slot. Must be called EXACTLY once per acquire (use try/finally). */
function releaseImageSlot(): void {
  activeImageFetches = Math.max(0, activeImageFetches - 1);
  pumpImageQueue();
}

export interface AuthedImageState {
  url?: string;
  status: "loading" | "loaded" | "error";
}

export function useAuthedImageUrl(serverPath: string | undefined): AuthedImageState {
  const [state, setState] = useState<AuthedImageState>({ status: "loading" });

  useEffect(() => {
    if (!serverPath) {
      setState({ status: "loading" });
      return;
    }
    // Extract last two segments: `_shared/123-foo.jpg` from
    // `/home/claude/agent/inbox/_shared/123-foo.jpg`. We only need
    // subdir + filename — the rest is hardcoded in the backend.
    const parts = serverPath.split("/").filter(Boolean);
    if (parts.length < 2) {
      setState({ status: "error" });
      return;
    }
    const filename = parts[parts.length - 1]!;
    const subdir = parts[parts.length - 2]!;

    let cancelled = false;
    let createdUrl: string | undefined;
    setState({ status: "loading" });
    (async () => {
      // Wait for a fetch slot (bounds the parallel-burst on image-heavy chats).
      await acquireImageSlot();
      try {
        if (cancelled) return; // unmounted / scrolled away while queued — skip
        const r = await authFetch(
          `/api/uploads/${encodeURIComponent(subdir)}/${encodeURIComponent(filename)}`,
        );
        if (!r.ok) {
          // 404 (file missing / relocated away / never written), 401 (token),
          // 5xx — surface as a broken-image state so the UI shows an
          // "unavailable" placeholder instead of spinning forever.
          if (!cancelled) setState({ status: "error" });
          return;
        }
        const blob = await r.blob();
        if (cancelled) return;
        // CRITICAL: even though we re-check `cancelled` BEFORE setState, the
        // URL is allocated by createObjectURL itself. If we early-return after
        // creating but before assigning to `createdUrl`, the cleanup function
        // can't see it and it leaks. So: create, re-check, revoke-and-bail if
        // cancelled, only then assign. Without this, rapid serverPath changes
        // (scrolling a 50-image chat on iOS) leak one URL per cancellation.
        const u = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        createdUrl = u;
        setState({ url: u, status: "loaded" });
      } catch {
        if (!cancelled) setState({ status: "error" });
      } finally {
        // Release EXACTLY once per acquire (this finally always runs once the
        // acquire above resolved) so a slot can't leak on cancel/error/success.
        releaseImageSlot();
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [serverPath]);

  return state;
}
